// Base redux functions.
import {
    SliceFactory,
    FetchCollectionData,
    FetchItemDataType,
    ApiCollectionResponse,
} from '@europrocurement/l2d-redux-utils';

// Api configuration.
import { isEmpty } from 'lodash';
import {
    GroupesDeTravailApi, // GroupesTravail Endpoint Class.
    GroupesDeTravailApiApiGroupestravailGetCollectionRequest as GroupesTravailRequestParams, // Endpoints params (Filters, orderBy...)
    Configuration as UtilisateursConfiguration, // Api utilisateurs configuration.
} from '../../../openApi/ApiUtilisateur';

/**
 * Types.
 * Import mutable type, entity object interface and API configuration parameters.
 */
import {
    type Mutable,
    type GroupeTravailUtilisateurApiObject, // GroupeTravail object interface.
    type UtilisateursConfigurationParameters, // Api utilisateurs configuration interface.
} from '../types';

// Lodash.

// Constants.
import { UTILISATEUR_REDUCER_NAME, GROUPE_TRAVAIL_SLICE_NAME } from '../constants';

export type GroupeTravail = GroupeTravailUtilisateurApiObject;

/**
 * Mapper.
 *
 * Function that transform Api objects into front compatible ones (DTO).
 * Mandatory for createSlice().
 */
const mapperGroupeTravail = function (workgroup: GroupeTravail) {
    return {
        ...workgroup,
    } as GroupeTravail;
};

/**
 * Function that fetch the collection of groupesTravail and store it into the global state Redux
 * @param configuration API configuration parameters
 * @returns the response of API request (get collection/get by id)
 */
export function createGroupeTravailSlice(configuration: UtilisateursConfigurationParameters) {
    // Create an Api utilisateurs configuration with interface.
    const conf = new UtilisateursConfiguration(configuration);
    const api = new GroupesDeTravailApi(conf);

    // Create getCollection callback.
    const fetchGroupeTravailCollection: FetchCollectionData<GroupeTravail> = ({
        pagination: { page, itemsPerPage },
        filters,
        search,
        orders,
    }) => {
        // Api groupesTravail getCollection Request Parameters (filters, search, orders...).
        const input: Mutable<GroupesTravailRequestParams> = {
            page: page + 1,
            itemsPerPage,
        };

        /**
         * Manage search filters.
         *
         * See api.ts for filters names and params.
         */
        if (filters.domaineDdmLibelle && filters.domaineDdmLibelle !== '') {
            input.domaineDdmLibelle = filters.domaineDdmLibelle as string;
        }

        if (filters.lib && filters.lib !== '') {
            input.lib = filters.lib as string;
        }

        // MultiFieldSearch.
        if (search && search !== '') {
            input.multiFieldSearch = search as string;
        }

        /**
         * Manage boolean filters.
         */
        if (filters.fDeleteStatus !== undefined) {
            input.fDeleteStatus = filters.fDeleteStatus as boolean;
        }

        /**
         * Manage order filters.
         */
        if (orders && orders !== null) {
            orders.forEach((order) => {
                if (!isEmpty(order.field) && !isEmpty(order.value)) {
                    switch (order.field) {
                        case 'domaineDdmLibelle':
                            input.orderDomaineDdmLibelle = order.value;
                            break;
                        case 'lib':
                            input.orderLib = order.value;
                            break;
                        case 'dCreation':
                            input.orderDCreation = order.value;
                            break;
                        default:
                    }
                }
            });
        }

        return api
            .apiGroupestravailGetCollection(input)
            .then((res) => res.data)
            .catch((error) => {
                console.error(error);
            }) as Promise<ApiCollectionResponse<GroupeTravail>>;
    };

    const fetchGroupeTravailItem: FetchItemDataType<GroupeTravail> = ({
        idItem,
        // pagination,
    }) =>
        api
            .apiGroupestravailIdGet({ id: String(idItem) })
            .then((res) => res.data) as Promise<GroupeTravail>;

    const { slice: groupeTravailSlice, dataSourcesThunks: groupeTravailDataSourcesThunks } =
        SliceFactory.createSlice<GroupeTravail, GroupeTravail>(
            GROUPE_TRAVAIL_SLICE_NAME,
            UTILISATEUR_REDUCER_NAME,
            [],
            fetchGroupeTravailCollection,
            fetchGroupeTravailItem,
            mapperGroupeTravail,
            {
                fDeleteStatus: false,
            },
        );

    return { groupeTravailSlice, groupeTravailDataSourcesThunks };
}
